import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { Label } from './tasksApi';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface LabelWithCount extends Label {
  taskCount: number;
}

// 'leads' = only the lead catalogue (labels made from the lead picker or put on
// a lead); the full list also carries every task label.
export type LabelScope = 'leads';

export async function listLabels(scope?: LabelScope): Promise<LabelWithCount[]> {
  const res = await apiFetch<Envelope<{ labels: LabelWithCount[] }>>(
    scope ? `/api/labels?scope=${scope}` : '/api/labels',
    { headers: authHeaders() },
  );
  return res.data.labels;
}

export async function createLabel(
  name: string,
  color?: string,
  scope?: LabelScope,
): Promise<Label> {
  const res = await apiFetch<Envelope<Label>>('/api/labels', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ name, ...(color ? { color } : {}), ...(scope ? { scope } : {}) }),
  });
  return res.data;
}

// Create a label via the project-scoped endpoint (CLE-182). Labels are a shared
// global catalogue; the project is validated before the label is created.
export async function createProjectLabel(
  projectId: string,
  name: string,
  color?: string,
): Promise<Label> {
  const res = await apiFetch<Envelope<Label>>(`/api/projects/${projectId}/labels`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ name, ...(color ? { color } : {}) }),
  });
  return res.data;
}

export async function updateLabel(
  id: string,
  input: { name?: string; color?: string },
): Promise<Label> {
  const res = await apiFetch<Envelope<Label>>(`/api/labels/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteLabel(id: string): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(`/api/labels/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}
